import React, { useState } from 'react'
import { Select as AntSelect, Tooltip } from 'antd'
import { SelectProps as AntSelectProps } from 'antd/lib/select'
import { Icon } from '@iconify/react'

import ConfirmationModal from '../../molecules/EditableSelect/Modal/ConfirmationModal'

import { Tag } from '../../atoms'

import styles from './_.module.css'

export interface EditableSelectProps extends AntSelectProps<string> {
  onSave?: () => void
  preventChange?: (values: string | undefined) => void
  isEditable?: boolean
  maxTagAllowed?: number
}

export const EditableSelect: React.FC<EditableSelectProps> = ({
  children,
  bordered,
  className,
  defaultValue,
  disabled = false,
  maxTagCount,
  onBlur,
  onChange,
  options,
  placeholder,
  optionFilterProp,
  size,
  value,
  onSave,
  preventChange,
  isEditable: __isEditable = true
}) => {
  const [isEditable, toggleEditable] = useState<boolean>(false)
  const [openConfirmationModal, toggleConfirmationModal] = useState(false)
  const [selectedTag, setSelectedTag] = useState<
    Record<string, any> | undefined
  >()
  const [deSelectedValue, setDeSelectedValue] = useState<number | undefined>()

  const tagRender = (props: Record<string, any>) => {
    const { label, value, closable } = props
    const selectedOption = options?.find(v => v.value === value)
    // if the tag is closable means that it is the maxCount placeholder
    if (!closable) {
      return <span className={styles.maxPlaceholder}>{label}&nbsp;</span>
    }

    return (
      <>
        <Tag
          className={styles.selectTag}
          color={selectedOption?.color || 'default'}
          closable={isEditable}
          onClose={() => {
            setSelectedTag(props)
            toggleConfirmationModal(!openConfirmationModal)
          }}
        >
          <Tooltip
            key="tag"
            placement="bottom"
            title={selectedOption?.tooltip ?? selectedOption?.label}
          >
            <span className={styles.selectTagLabel}>{label}&nbsp;</span>
          </Tooltip>
        </Tag>
      </>
    )
  }

  const handleDeSelect = (deSelectedValue: any) => {
    setDeSelectedValue(deSelectedValue)
    if (preventChange) preventChange(value)
    const option = options?.find(opt => opt.value === deSelectedValue)
    setSelectedTag(option)
    toggleConfirmationModal(isOpen => !isOpen)
  }

  const handleRemoveOptions = () => {
    const removedId: number | undefined = !deSelectedValue
      ? selectedTag?.value
      : deSelectedValue

    const items: any =
      value && Array.isArray(value)
        ? value.filter(id => id !== removedId)
        : undefined
    if (onChange && items) onChange(items, [])
    setDeSelectedValue(undefined)
    setSelectedTag(undefined)
    toggleConfirmationModal(!openConfirmationModal)
  }

  const tooltipTitle = !__isEditable ? 'Editable after selection' : ''

  return (
    <div className={styles.selectContainer}>
      {openConfirmationModal ? (
        <ConfirmationModal
          body={<p>Are you sure? You want to remove {selectedTag?.label}</p>}
          isOpen={openConfirmationModal}
          title={`Cancel ${selectedTag?.label}`}
          okText="Delete"
          onOk={handleRemoveOptions}
          onCancel={() => {
            setSelectedTag(undefined)
            setDeSelectedValue(undefined)
            toggleConfirmationModal(!openConfirmationModal)
          }}
        />
      ) : (
        ''
      )}
      <AntSelect
        className={className}
        bordered={bordered}
        defaultValue={defaultValue}
        disabled={disabled}
        mode="multiple"
        onBlur={onBlur}
        options={options}
        optionFilterProp={optionFilterProp}
        placeholder={placeholder}
        showArrow={false}
        showSearch={false}
        size={size}
        maxTagCount={maxTagCount}
        maxTagPlaceholder={item => {
          return `+${item.length}`
        }}
        tagRender={tagRender}
        value={value}
        onChange={onChange}
        onDeselect={handleDeSelect}
        dropdownClassName={!isEditable ? styles.hideDropDown : ''}
      >
        {children}
      </AntSelect>
      <span className={styles.actionIcons}>
        {isEditable && (
          <div
            className={styles.saveButton}
            onClick={() => {
              toggleEditable(!isEditable)
              if (onSave) onSave()
            }}
            aria-hidden="true"
          >
            <Icon icon="ant-design:save-outlined" width="18" height="18" />
          </div>
        )}
        {!isEditable && !disabled && (
          <Tooltip title={tooltipTitle}>
            <Icon
              icon="ant-design:edit-outlined"
              width="18"
              height="18"
              className={!__isEditable ? styles.disableIcon : ''}
              onClick={() => {
                if (__isEditable) toggleEditable(!isEditable)
              }}
            />
          </Tooltip>
        )}
      </span>
    </div>
  )
}
